import React, { useEffect, useState } from "react";
import { Panel } from "primereact/panel";

// PrimeReact keeps its header-template options type internal, so describe the
// parts we actually use rather than reaching for an unexported interface.
interface PanelHeaderOptions {
  className: string;
  titleClassName: string;
  onTogglerClick: (event: React.MouseEvent<HTMLElement>) => void;
  togglerElement: JSX.Element;
}

const STORAGE_PREFIX = "section_collapsed:";

// The open/closed choice is a pure UI preference, so it lives in localStorage
// rather than in state that resets on every visit. Storage can throw (private
// windows, blocked site data), in which case we fall back to the default.
const readCollapsed = (storageKey: string, fallback: boolean) => {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + storageKey);
    return stored === null ? fallback : stored === "true";
  } catch {
    return fallback;
  }
};

interface CollapsibleSectionProps {
  /** Heading shown in the section header. */
  title: string;
  /** Stable key the open/closed choice is remembered under. */
  storageKey: string;
  /** Status shown next to the title — stays visible while collapsed. */
  badge?: React.ReactNode;
  /** Controls rendered on the right of the header, before the toggle. */
  actions?: React.ReactNode;
  defaultCollapsed?: boolean;
  className?: string;
  children: React.ReactNode;
}

export default function CollapsibleSection({
  title,
  storageKey,
  badge,
  actions,
  defaultCollapsed = false,
  className,
  children,
}: CollapsibleSectionProps) {
  const [collapsed, setCollapsed] = useState(() =>
    readCollapsed(storageKey, defaultCollapsed)
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_PREFIX + storageKey, String(collapsed));
    } catch {
      // Remembering the choice is best-effort; the section still works without it.
    }
  }, [storageKey, collapsed]);

  // The whole header toggles, not just the chevron. The actions sit inside a
  // wrapper that stops the click from bubbling, so pressing one of them doesn't
  // also collapse the section. The toggle button keeps its own handler and
  // stays the keyboard/screen-reader control.
  const headerTemplate = (options: PanelHeaderOptions) => (
    <div
      className={options.className}
      onClick={options.onTogglerClick}
      style={{ cursor: "pointer" }}
    >
      <div className="flex align-items-center gap-3">
        <span className={options.titleClassName}>{title}</span>
        {badge}
      </div>
      <div
        className="flex align-items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {actions}
        {options.togglerElement}
      </div>
    </div>
  );

  return (
    <Panel
      className={className ? `mb-4 ${className}` : "mb-4"}
      toggleable
      collapsed={collapsed}
      onToggle={(e) => setCollapsed(e.value)}
      headerTemplate={headerTemplate}
      expandIcon="pi pi-chevron-right"
      collapseIcon="pi pi-chevron-down"
    >
      {children}
    </Panel>
  );
}
